import type { AxiosRequestConfig, GenericAbortSignal } from 'axios'
import { API_EMPLOYEE_URL } from '../constants/base-urls'
import type {
  ICareerChange,
  IEmployee,
  IFamily,
  IIdentityInformation,
  IPaginationParam,
  IPaginationResponse,
  IPayrollEmployee,
  ITaxStatus,
} from '../types'
import { createAxiosInstance, mergeParamsWithConfig } from '../utils'

type FetchEmployeesParams = {
  scheduleId?: string
  branchId?: string
  departmentId?: string
  shiftPolicyId?: string
  startDate?: string
  endDate?: string
  date?: string
  shiftId?: string
  isCorrection?: boolean
} & IPaginationParam

const axios = createAxiosInstance({
  baseURL: API_EMPLOYEE_URL,
  withAuth: true,
})

/**
 * Employee
 *
 */
export const fetchEmployees = (params: FetchEmployeesParams = {}, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IEmployee> }>(`/employee`, { params: { ...params, status: 'ACTIVE' }, signal })
    .then((response) => response.data.data)
}

export const fetchEmployeesShift = (params: FetchEmployeesParams = {}, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IEmployee> }>(`/employee-shift/employee`, { params: { ...params }, signal })
    .then((response) => response.data.data)
}

export const fetchEmployeesLeave = (params: FetchEmployeesParams = {}, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IEmployee> }>(`/employee-leave/employee`, { params: { ...params }, signal })
    .then((response) => response.data.data)
}

export const fetchEmployeesAttendance = (params: FetchEmployeesParams = {}, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IEmployee> }>(`/attendance/employee`, { params: { ...params }, signal })
    .then((response) => response.data.data)
}

export const fetchPreviousEmployees = (params: FetchEmployeesParams = {}) => {
  return axios
    .get<{ data: IPaginationResponse<IEmployee> }>(`/employee`, { params: { ...params, status: 'DEACTIVE' } })
    .then((response) => response.data.data)
}

export const fetchEmployee = (
  oid: string,
  data: 'personal' | 'family' | 'document' | 'address' | 'career' | 'bank' | 'payroll',
  config?: AxiosRequestConfig,
) => {
  return axios.get<{ data: IEmployee }>(`/employee/${oid}`, mergeParamsWithConfig({ data }, config)).then((response) => response.data.data)
}

export const createEmployee = (payload: Record<string, any>) => {
  return axios.post<{ data: IEmployee }>(`/employee`, payload).then((response) => response.data.data)
}

export const updateEmployee = (
  oid: string,
  data: 'personal' | 'family' | 'address' | 'career' | 'bank' | 'payroll',
  payload: Record<string, any>,
) => {
  return axios.put<{ data: IEmployee }>(`/employee/${oid}?data=${data}`, payload).then((response) => response.data.data)
}

export const createInitEmployee = (payload: Record<string, any>) => {
  return axios.post<{ data: IEmployee }>(`/employee/init`, payload).then((response) => response.data.data)
}

export const deleteEmployee = (id: string) => {
  return axios.delete(`/employee/${id}`).then((response) => response.data.data)
}

export const setInactiveEmployee = (payload: Record<string, any>) => {
  return axios.patch(`/employee/inactive`, payload).then((response) => response.data.data)
}

export const restoreEmployee = (id: string) => {
  return axios.patch(`/employee/restore-inactive/${id}`).then((response) => response.data.data)
}

export const isEmployeeActive = (oid: string) => {
  return axios.get<{ data: { active: boolean } }>(`/employee/${oid}/active`).then((response) => response.data.data)
}

export const fetchFamilies = (params: IPaginationParam & { employeeId: string }, config?: AxiosRequestConfig) => {
  return axios
    .get<{
      data: IPaginationResponse<IFamily>
    }>(`/employee-families`, mergeParamsWithConfig(params, { ...config, headers: { employee_id: params.employeeId } }))
    .then((response) => response.data.data)
}

export const fetchFamily = (employeeId: string, familyId: string) => {
  return axios.get(`employee-families/${familyId}`, { headers: { employee_id: employeeId } }).then((response) => response.data.data)
}

export const removeFamily = (employeeId: string, familyId: string) => {
  return axios.delete(`/employee/${employeeId}/remove-family-members?familyId=${familyId}`)
}

/**
 * Career Changes
 *
 */
export const fetchCareerChanges = (params: IPaginationParam & { employeeId: string }, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<ICareerChange> }>(`/career-change`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const fetchCareerChange = (careerChangeId: string, config?: AxiosRequestConfig) => {
  return axios.get<{ data: ICareerChange }>(`/career-change/${careerChangeId}`, config).then((response) => response.data.data)
}

export const createCareerChange = (payload: Record<string, any>) => {
  return axios.post<{ data: ICareerChange }>(`/career-change`, payload).then((response) => response.data.data)
}

export const updateCareerChange = (careerChangeId: string, payload: Record<string, any>) => {
  return axios.put<{ data: ICareerChange }>(`/career-change/${careerChangeId}`, payload).then((response) => response.data.data)
}

export const deleteCareerChange = (careerChangeId: string) => {
  return axios.delete(`/career-change/${careerChangeId}`).then((response) => response.data.data)
}

export const isEmployeCodeExists = (value: string) => {
  return axios.get(`/employee/check-unique`, { params: { field: 'employeeCode', value } }).then((response) => response.data.data)
}

export const isAttendanceIdExists = (value: string) => {
  return axios.get(`/employee/check-unique`, { params: { field: 'attendanceId', value } }).then((response) => response.data.data)
}

/**
 * Identity Information
 */
export const fetchIdentityInformations = (params: IPaginationParam & { employeeId: string }, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<IIdentityInformation> }>(`/identity`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const fetchIdentityInformation = (identityId: string) => {
  return axios.get<{ data: IIdentityInformation }>(`/identity/${identityId}`).then((response) => response.data.data)
}

export const createIdentityInformation = (payload: Record<string, any>) => {
  return axios.post<{ data: IIdentityInformation }>(`/identity`, payload).then((response) => response.data.data)
}

export const updateIdentityInformation = (identityId: string, payload: Record<string, any>) => {
  return axios.put<{ data: IIdentityInformation }>(`/identity/${identityId}`, payload).then((response) => response.data.data)
}

export const deleteIdentityInformation = (identityId: string) => {
  return axios.delete(`/identity/${identityId}`).then((response) => response.data.data)
}

export const fetchTaxStatus = (params: IPaginationParam & { employeeId: string }, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<ITaxStatus> }>(`employee/tax-status`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const createTaxStatus = (payload: Record<string, any>) => {
  return axios.post<{ data: ITaxStatus }>(`employee/tax-status`, payload).then((response) => response.data.data)
}

export const updateTaxStatus = (taxStatusId: string, payload: Record<string, any>) => {
  return axios.put<{ data: ITaxStatus }>(`employee/tax-status/${taxStatusId}`, payload).then((response) => response.data.data)
}

export const deleteTaxStatus = (taxStatusId: string) => {
  return axios.delete(`employee/tax-status/${taxStatusId}`).then((response) => response.data.data)
}

/**
 * Payroll Employee
 */
export const fetchPayrollEmployee = (params: IPaginationParam & { employeeId: string }, config?: AxiosRequestConfig) => {
  return axios
    .get<{ data: IPaginationResponse<IPayrollEmployee> }>(`/employee/payroll-policy`, mergeParamsWithConfig(params, config))
    .then((response) => response.data.data)
}

export const updatePayrollEmployee = (employeeId: string, payload: Record<string, any>) => {
  return axios.put<{ data: IPayrollEmployee }>(`/employee/payroll-policy/${employeeId}`, payload).then((response) => response.data.data)
}
